import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { AuthState } from "./auth";

export interface DeviceState {
  id: string;
  datetime_created: string;
  updated_by: string;
  name: string;
  model: [string, string];
  serial_number: string | null;
  associated_room: [string, string] | null;
}

export interface PaginationProps {
  has_previous: boolean;
  has_next: boolean;
  current_page: number;
  total_pages: number;
  total_objects: number;
}

// Define a type for the slice state
export interface DevicesState {
  devices: {
    data: DeviceState[] | null;
    connectivity: {
      data: Array<{ id: string; online: boolean }> | null;
      loading: boolean;
      waiting: boolean;
      errors: string[];
    };
    pagination: PaginationProps;
    loading: boolean;
    waiting: boolean;
    errors: string[];
  };
  options: {
    [key: string]: any;
  };
}

// Define the initial state using that type
const initialState: DevicesState = {
  devices: {
    data: null,
    connectivity: {
      data: null,
      loading: false,
      waiting: false,
      errors: [],
    },
    pagination: {
      has_previous: false,
      has_next: false,
      current_page: 1,
      total_pages: 1,
      total_objects: 0,
    },
    loading: false,
    waiting: false,
    errors: [],
  },
  options: {},
};

//////////////////////////////
//////// Async Thunks ////////
//////////////////////////////

// Get devices
export const getDevices = createAsyncThunk(
  "devices/get",
  async (
    obj: {
      pageNum: number | boolean;
      model: string | null;
    },
    { getState, rejectWithValue },
  ) => {
    try {
      // Get user data from store
      const { auth } = getState() as { auth: AuthState };

      // Configure authorization header with user's token
      const config = { headers: { Authorization: `Bearer ${auth.access}` } };

      // Build endpoint URL
      let url = `/api/device/?page=${obj.pageNum}`;
      if (obj.model) url += `&model=${obj.model}`;

      // Send request
      const res = await axios.get(url, config);
      return { data: res.data, status: res.status };
    } catch (err: any) {
      if (err.response && err.response.data.message) {
        return rejectWithValue(err.response.data.message);
      } else {
        return rejectWithValue(err.message);
      }
    }
  },
);

// Get devices connectivity
export const getDevicesConnectivity = createAsyncThunk(
  "devices/connectivity/get",
  async (
    obj: {
      pageNum: number | boolean;
      model: string | null;
    },
    { getState, rejectWithValue },
  ) => {
    try {
      // Get user data from store
      const { auth } = getState() as { auth: AuthState };

      // Configure authorization header with user's token
      const config = { headers: { Authorization: `Bearer ${auth.access}` } };

      // Build endpoint URL
      let url = `/api/device/connectivity/?page=${obj.pageNum}`;
      if (obj.model) url += `&model=${obj.model}`;

      // Send request
      const res = await axios.get(url, config);
      return { data: res.data, status: res.status };
    } catch (err: any) {
      if (err.response && err.response.data.message) {
        return rejectWithValue(err.response.data.message);
      } else {
        return rejectWithValue(err.message);
      }
    }
  },
);

// Get devices connectivity batch
export const getDevicesConnectivityBatch = createAsyncThunk(
  "devices/connectivity/batch/get",
  async (
    obj: {
      deviceIds: string[];
      model: string | null;
    },
    { getState, rejectWithValue },
  ) => {
    try {
      // Get user data from store
      const { auth } = getState() as { auth: AuthState };

      // Configure authorization header with user's token
      const config = { headers: { Authorization: `Bearer ${auth.access}` } };

      // Build endpoint URL
      let url = `/api/device/connectivity/batch/`;
      if (obj.model) url += `?model=${obj.model}`;

      // Build body
      const body = { id: obj.deviceIds };

      // Send request
      const res = await axios.post(url, body, config);
      return { data: res.data, status: res.status };
    } catch (err: any) {
      if (err.response && err.response.data.message) {
        return rejectWithValue(err.response.data.message);
      } else {
        return rejectWithValue(err.message);
      }
    }
  },
);

// Update device
export const updateDevice = createAsyncThunk(
  "device/update/put",
  async (
    obj: {
      deviceId: string;
      name: string;
      property: string;
      model: string;
      serialNumber: string | null;
      associatedRoom: string | null;
    },
    { getState, rejectWithValue },
  ) => {
    try {
      // Get user data from store
      const { auth } = getState() as { auth: AuthState };

      // Configure authorization header with user's token
      const config = { headers: { Authorization: `Bearer ${auth.access}` } };

      // Build body
      const body = {
        id: obj.deviceId,
        name: obj.name,
        property: obj.property,
        model: obj.model,
        serial_number: obj.serialNumber,
        associated_room: obj.associatedRoom,
      };
      const res = await axios.put("/api/device/", body, config);
      return { data: res.data, status: res.status };
    } catch (err: any) {
      if (err.response && err.response.data.message) {
        return rejectWithValue(err.response.data.message);
      } else {
        return rejectWithValue(err.message);
      }
    }
  },
);

/////////////////////////////
//////// Redux slice ////////
/////////////////////////////

const devicesSlice = createSlice({
  name: "devices",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder

      // Get devices
      .addCase(getDevices.pending, (state) => {
        state.devices.loading = true;
        state.devices.waiting = false;
      })
      .addCase(getDevices.fulfilled, (state, action) => {
        state.devices.data = action.payload.data.data;
        state.devices.pagination = action.payload.data.pagination;
        state.options = action.payload.data.options;
        state.devices.loading = false;
        state.devices.waiting = false;
      })
      .addCase(getDevices.rejected, (state) => {
        state.devices.loading = false;
        state.devices.waiting = false;
      })

      // Get devices connectivity
      .addCase(getDevicesConnectivity.pending, (state) => {
        state.devices.connectivity.loading = true;
        state.devices.connectivity.waiting = true;
      })
      .addCase(getDevicesConnectivity.fulfilled, (state, action) => {
        state.devices.connectivity.data = action.payload.data.data;
        state.devices.connectivity.loading = false;
        state.devices.connectivity.waiting = false;
      })
      .addCase(getDevicesConnectivity.rejected, (state) => {
        state.devices.connectivity.loading = false;
        state.devices.connectivity.waiting = false;
      })

      // Get devices connectivity batch
      .addCase(getDevicesConnectivityBatch.pending, (state) => {
        state.devices.connectivity.loading = true;
        state.devices.connectivity.waiting = true;
      })
      .addCase(getDevicesConnectivityBatch.fulfilled, (state, action) => {
        state.devices.connectivity.data = action.payload.data.data;
        state.devices.connectivity.loading = false;
        state.devices.connectivity.waiting = false;
      })
      .addCase(getDevicesConnectivityBatch.rejected, (state) => {
        state.devices.connectivity.loading = false;
        state.devices.connectivity.waiting = false;
      })

      // Update device
      .addCase(updateDevice.pending, (state) => {
        state.devices.waiting = true;
        state.devices.loading = true;
      })
      .addCase(updateDevice.fulfilled, (state, action) => {
        const previousData =
          state.devices.data === null ? [] : state.devices.data;
        const newData = [];
        for (let i = 0; i < previousData.length; i++) {
          const device = previousData[i];
          if (device.id === action.payload.data.id) {
            newData.push(action.payload.data);
          } else {
            newData.push(device);
          }
        }
        state.devices.data = newData;
        state.devices.loading = false;
        state.devices.waiting = false;
      })
      .addCase(updateDevice.rejected, (state) => {
        state.devices.waiting = false;
        state.devices.loading = false;
      });
  },
});

export default devicesSlice.reducer;
